import { createGlobalStyle } from 'styled-components';

// Self-hosted fonts - eliminates 801ms Google Fonts critical path
import './fonts';

export const GlobalStyles = createGlobalStyle<{ isDark: boolean }>`

  :root {
    --qb-accent: #0A5DEB;
    --qb-accent-soft: #3B7FFF;
    --qb-ink: #0F172A;
    --qb-surface: #FFFFFF;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    height: 100%;
  }

  body {
    min-height: 100%;
    font-family: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: ${props => props.theme.typography.fontSize.base};
    line-height: ${props => props.theme.typography.lineHeight.normal};
    color: ${props => props.isDark ? props.theme.colors.dark.text : props.theme.colors.light.text};
    background-color: ${props => props.isDark ? props.theme.colors.dark.background : props.theme.colors.light.background};
    transition: color ${props => props.theme.transitions.base},
      background-color ${props => props.theme.transitions.base};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: clip;
    position: relative;
  }

  #root {
    position: relative;
    min-height: 100vh;
    z-index: 2;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Cabin Variable', 'Cabin', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-weight: 700;
    line-height: ${props => props.theme.typography.lineHeight.tight};
    margin-bottom: ${props => props.theme.spacing[4]};
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: clamp(2.5rem, 5vw, ${props => props.theme.typography.fontSize['6xl']});
    font-weight: 800;

    @media (max-width: ${props => props.theme.breakpoints.md}) {
      font-size: clamp(2rem, 6vw, ${props => props.theme.typography.fontSize['4xl']});
    }
  }

  h2 {
    font-size: clamp(2rem, 4vw, ${props => props.theme.typography.fontSize['4xl']});

    @media (max-width: ${props => props.theme.breakpoints.md}) {
      font-size: clamp(1.75rem, 5vw, ${props => props.theme.typography.fontSize['3xl']});
    }
  }

  h3 {
    font-size: clamp(1.5rem, 3vw, ${props => props.theme.typography.fontSize['3xl']});

    @media (max-width: ${props => props.theme.breakpoints.md}) {
      font-size: clamp(1.25rem, 4vw, ${props => props.theme.typography.fontSize['2xl']});
    }
  }

  h4 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};

    @media (max-width: ${props => props.theme.breakpoints.md}) {
      font-size: ${props => props.theme.typography.fontSize.xl};
    }
  }

  h5 {
    font-size: ${props => props.theme.typography.fontSize.xl};
  }

  h6 {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }

  p {
    margin-bottom: ${props => props.theme.spacing[4]};
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};

    &:hover {
      color: ${props => props.theme.colors.primaryDark};
    }

    &:focus {
      outline: 2px solid ${props => props.theme.colors.primary};
      outline-offset: 2px;
    }
  }

  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;

    &:focus {
      outline: 2px solid ${props => props.theme.colors.primary};
      outline-offset: 2px;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;

    &:focus {
      outline: 2px solid ${props => props.theme.colors.primary};
      outline-offset: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  ul, ol {
    list-style: none;
  }

  ::selection {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }

  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.isDark ? props.theme.colors.dark.backgroundSecondary : props.theme.colors.light.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.isDark ? props.theme.colors.dark.border : props.theme.colors.light.border};
    border-radius: ${props => props.theme.borderRadius.full};
    transition: background ${props => props.theme.transitions.fast};
    border: 3px solid ${props => props.isDark ? props.theme.colors.dark.backgroundSecondary : props.theme.colors.light.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  a, button, [role="button"] {
    cursor: pointer;
  }

  body::before {
    content: '';
    position: fixed;
    inset: 0;
    opacity: ${props => props.isDark ? 0.015 : 0.02};
    z-index: 1;
    pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='1' /%3E%3C/svg%3E");
  }
`;
